/* =====================================================================
   sn-product.js — fiche produit SN (JavaScript sans dépendance)
   - <sn-gallery>     : galerie à défilement natif, flèches, vignettes
   - <sn-bundle>      : paliers de lots → quantité du formulaire + prix du bouton
   - <sn-sticky-atc>  : barre d'achat collante (IntersectionObserver)
   - <sn-xsell>       : « Les clients achètent aussi… » → /cart/add.js + tiroir panier Dawn
   - changement de variante (événement Dawn) : rafraîchit lots, barre collante, image
   - lien de la note : repli vers la section d'avis si l'ancre n'existe pas
   ===================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var smooth = reduceMotion ? 'auto' : 'smooth';

  function headerOffset() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    var value = parseFloat(raw);
    return isNaN(value) ? 0 : value;
  }

  function scrollToElement(el) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.scrollY - headerOffset() - 12;
    window.scrollTo({ top: Math.max(top, 0), behavior: smooth });
  }

  /* -------------------------------------------------------------------
     Galerie
     ------------------------------------------------------------------- */
  if (!customElements.get('sn-gallery')) {
    customElements.define(
      'sn-gallery',
      class SnGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.sn-gallery__track');
          if (!this.track) return;
          this.slides = Array.prototype.slice.call(this.track.children);
          this.thumbs = Array.prototype.slice.call(this.querySelectorAll('.sn-gallery__thumb'));
          this.thumbRow = this.querySelector('.sn-gallery__thumbs');
          this.index = 0;
          this.raf = null;

          this.querySelectorAll('[data-sn-dir]').forEach((button) => {
            button.addEventListener('click', () => this.go(this.index + Number(button.dataset.snDir)));
          });
          this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));
          this.track.addEventListener(
            'scroll',
            () => {
              if (this.raf) cancelAnimationFrame(this.raf);
              this.raf = requestAnimationFrame(() => this.onScroll());
            },
            { passive: true }
          );
          window.addEventListener('resize', () => this.go(this.index, 'auto'), { passive: true });

          var start = parseInt(this.dataset.start || '0', 10) || 0;
          if (start > 0) {
            requestAnimationFrame(() => this.go(start, 'auto'));
          } else {
            this.update();
          }
        }

        maxScroll() {
          return this.track.scrollWidth - this.track.clientWidth;
        }

        onScroll() {
          var left = this.track.scrollLeft;
          var best = 0;
          var distance = Infinity;
          this.slides.forEach((slide, i) => {
            var d = Math.abs(slide.offsetLeft - left);
            if (d < distance) {
              distance = d;
              best = i;
            }
          });
          // En fin de piste, la dernière image ne peut pas s'aligner à gauche : on la considère active.
          if (left >= this.maxScroll() - 2) best = this.slides.length - 1;
          if (best !== this.index) {
            this.index = best;
            this.update();
          }
        }

        go(i, behavior) {
          if (!this.slides.length) return;
          var target = Math.max(0, Math.min(i, this.slides.length - 1));
          this.index = target;
          var left = Math.min(this.slides[target].offsetLeft, this.maxScroll());
          this.track.scrollTo({ left: left, behavior: behavior || smooth });
          this.update();
        }

        goToMedia(mediaId) {
          var i = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
          if (i > -1) this.go(i);
        }

        update() {
          var last = this.slides.length - 1;
          this.querySelectorAll('[data-sn-dir="-1"]').forEach((b) => {
            b.disabled = this.index <= 0;
          });
          this.querySelectorAll('[data-sn-dir="1"]').forEach((b) => {
            b.disabled = this.index >= last;
          });
          this.thumbs.forEach((thumb, i) => {
            if (i === this.index) {
              thumb.setAttribute('aria-current', 'true');
            } else {
              thumb.removeAttribute('aria-current');
            }
          });
          var active = this.thumbs[this.index];
          if (active && this.thumbRow) {
            var item = active.parentElement;
            var rowLeft = this.thumbRow.scrollLeft;
            var rowWidth = this.thumbRow.clientWidth;
            if (item.offsetLeft < rowLeft || item.offsetLeft + item.offsetWidth > rowLeft + rowWidth) {
              this.thumbRow.scrollTo({ left: item.offsetLeft - (rowWidth - item.offsetWidth) / 2, behavior: smooth });
            }
          }
        }
      }
    );
  }

  /* -------------------------------------------------------------------
     Lots : le palier choisi règle la quantité et le prix du bouton
     ------------------------------------------------------------------- */
  if (!customElements.get('sn-bundle')) {
    customElements.define(
      'sn-bundle',
      class SnBundle extends HTMLElement {
        connectedCallback() {
          this.currentIndex = null;
          this.addEventListener('change', (event) => {
            if (event.target.classList.contains('sn-bundle__input')) this.apply();
          });
          this.apply();
        }

        selected() {
          return this.querySelector('.sn-bundle__input:checked') || this.querySelector('.sn-bundle__input');
        }

        apply() {
          var input = this.selected();
          if (!input) return;
          if (!input.checked) input.checked = true;
          this.currentIndex = input.dataset.index;
          var form = document.getElementById(this.dataset.formId);
          var qtyField = form ? form.querySelector('[data-sn-qty]') : null;
          if (qtyField) qtyField.value = input.dataset.qty;
          document.querySelectorAll('[data-sn-atc-price="' + this.dataset.section + '"]').forEach((el) => {
            el.textContent = input.dataset.price;
          });
          this.dispatchEvent(
            new CustomEvent('sn:bundle-change', {
              bubbles: true,
              detail: { quantity: Number(input.dataset.qty), price: input.dataset.price, cents: Number(input.dataset.priceCents) },
            })
          );
        }

        /* Après un changement de variante, la liste est réécrite : on garde le palier choisi. */
        restore() {
          if (this.currentIndex) {
            var input = this.querySelector('.sn-bundle__input[data-index="' + this.currentIndex + '"]');
            if (input) input.checked = true;
          }
          this.apply();
        }
      }
    );
  }

  /* -------------------------------------------------------------------
     Barre d'achat collante
     ------------------------------------------------------------------- */
  if (!customElements.get('sn-sticky-atc')) {
    customElements.define(
      'sn-sticky-atc',
      class SnStickyAtc extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('.sn-sticky__btn');
          this.target = document.getElementById(this.dataset.target);
          this.visible = false;
          if (this.target) {
            if ('IntersectionObserver' in window) {
              this.observer = new IntersectionObserver((entries) => {
                var entry = entries[0];
                this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
              });
              this.observer.observe(this.target);
            }
            // Un saut de défilement (ancre, retour arrière) peut franchir le bouton sans qu'il soit
            // jamais visible : l'observateur ne se déclenche pas. On vérifie donc aussi au défilement.
            this.onScroll = () => {
              if (this.raf) return;
              this.raf = requestAnimationFrame(() => {
                this.raf = null;
                this.toggle(this.target.getBoundingClientRect().bottom < 0);
              });
            };
            window.addEventListener('scroll', this.onScroll, { passive: true });
            this.onScroll();
          }
          if (this.button) this.button.addEventListener('click', () => this.onClick());
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        }

        toggle(show) {
          if (show === this.visible) return;
          this.visible = show;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
          if (show) {
            this.removeAttribute('inert');
          } else {
            this.setAttribute('inert', '');
          }
        }

        onClick() {
          if (this.dataset.action === 'add') {
            var form = document.getElementById(this.dataset.formId);
            if (form && typeof form.requestSubmit === 'function') {
              form.requestSubmit();
              return;
            }
          }
          var anchor = document.getElementById(this.dataset.scrollTarget);
          if (!anchor) return;
          scrollToElement(anchor);
          var focusable = anchor.querySelector('.sn-bundle__input:checked, .sn-atc');
          if (focusable) focusable.focus({ preventScroll: true });
        }
      }
    );
  }

  /* -------------------------------------------------------------------
     « Les clients achètent aussi… » (<sn-xsell>) : rail de cartes,
     points + flèches, variantes par option, ajout via /cart/add.js
     ------------------------------------------------------------------- */
  function cartDrawer() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function addToCart(variantId, quantity) {
    var drawer = cartDrawer();
    var body = { items: [{ id: Number(variantId), quantity: quantity || 1 }] };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((s) => s.section || s.id);
      body.sections_url = window.location.pathname;
    }
    var root = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return fetch(root + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) throw new Error(data.description || data.message || 'Erreur');
        return data;
      });
  }

  function showCart(data) {
    var drawer = cartDrawer();
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sn-xsell', cartData: data });
    }
    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
      drawer.renderContents(data);
    } else {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }
  }

  if (!customElements.get('sn-xsell')) {
    customElements.define(
      'sn-xsell',
      class SnXsell extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-sn-xsell-track]');
          if (!this.track || this.ready) return;
          this.ready = true;
          this.nav = this.querySelector('[data-sn-xsell-nav]');
          this.dotsWrap = this.querySelector('[data-sn-xsell-dots]');
          this.prev = this.querySelector('[data-sn-xsell-prev]');
          this.next = this.querySelector('[data-sn-xsell-next]');
          this.cards = Array.prototype.slice.call(this.track.querySelectorAll('[data-sn-xsell-card]'));
          this.sync = this.sync.bind(this);
          this.buildDots();
          this.track.addEventListener('scroll', this.sync, { passive: true });
          if (this.prev) this.prev.addEventListener('click', () => this.go(this.index() - 1));
          if (this.next) this.next.addEventListener('click', () => this.go(this.index() + 1));
          this.addEventListener('change', this.onChange.bind(this));
          this.addEventListener('click', this.onAdd.bind(this));
          if ('ResizeObserver' in window) {
            this.observer = new ResizeObserver(this.sync);
            this.observer.observe(this.track);
          }
          this.sync();
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        buildDots() {
          if (!this.dotsWrap) return;
          this.dotsWrap.innerHTML = '';
          this.dots = this.cards.map((card, i) => {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'sn-xsell__dot';
            dot.setAttribute('aria-label', 'Article ' + (i + 1) + ' sur ' + this.cards.length);
            dot.addEventListener('click', () => this.go(i));
            this.dotsWrap.appendChild(dot);
            return dot;
          });
        }

        maxScroll() {
          return this.track.scrollWidth - this.track.clientWidth;
        }

        offsetOf(i) {
          return this.cards[i].offsetLeft - this.cards[0].offsetLeft;
        }

        index() {
          if (!this.cards.length) return 0;
          var position = this.track.scrollLeft;
          if (this.maxScroll() > 0 && position >= this.maxScroll() - 2) return this.cards.length - 1;
          var best = 0;
          var bestDistance = Infinity;
          for (var i = 0; i < this.cards.length; i++) {
            var distance = Math.abs(this.offsetOf(i) - position);
            if (distance < bestDistance) {
              bestDistance = distance;
              best = i;
            }
          }
          return best;
        }

        go(i) {
          if (!this.cards.length) return;
          var target = Math.max(0, Math.min(this.cards.length - 1, i));
          this.track.scrollTo({ left: Math.min(this.offsetOf(target), this.maxScroll()), behavior: smooth });
        }

        sync() {
          var scrollable = this.cards.length > 1 && this.maxScroll() > 2;
          if (this.nav) this.nav.hidden = !scrollable;
          var current = this.index();
          (this.dots || []).forEach((dot, i) => {
            dot.classList.toggle('is-active', i === current);
            if (i === current) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
          if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
          if (this.next) this.next.disabled = this.track.scrollLeft >= this.maxScroll() - 2;
        }

        onChange(event) {
          var select = event.target.closest('[data-sn-xsell-option]');
          if (!select) return;
          var card = select.closest('[data-sn-xsell-card]');
          var script = card && card.querySelector('[data-sn-xsell-variants]');
          if (!script) return;
          var variants;
          try {
            variants = JSON.parse(script.textContent);
          } catch (e) {
            return;
          }
          var chosen = Array.prototype.map.call(card.querySelectorAll('[data-sn-xsell-option]'), (s) => s.value);
          var variant = variants.find((v) => v.options.every((value, i) => value === chosen[i]));
          var available = !!(variant && variant.available);
          var button = card.querySelector('[data-sn-xsell-add]');
          if (button) {
            if (variant) button.dataset.variantId = variant.id;
            button.disabled = !available;
            var label = button.querySelector('.sn-xsell__add-label');
            if (label && label.dataset.label) label.textContent = available ? label.dataset.label : label.dataset.soldOut;
          }
          var price = card.querySelector('[data-sn-xsell-price]');
          if (!price || !variant) return;
          var now = price.querySelector('[data-sn-xsell-now]');
          var compare = price.querySelector('.sn-xsell__compare');
          if (now) now.textContent = variant.price;
          if (variant.compare) {
            if (!compare) {
              compare = document.createElement('s');
              compare.className = 'sn-xsell__compare';
              price.insertBefore(compare, now);
            }
            compare.innerHTML = '<span class="visually-hidden">Prix habituel :</span> ';
            compare.appendChild(document.createTextNode(variant.compare));
          } else if (compare) {
            compare.remove();
          }
        }

        onAdd(event) {
          var button = event.target.closest('[data-sn-xsell-add]');
          if (!button || button.disabled || !button.dataset.variantId) return;
          var card = button.closest('[data-sn-xsell-card]');
          var error = card ? card.querySelector('.sn-xsell__error') : null;
          if (error) error.hidden = true;
          button.disabled = true;
          button.classList.add('is-loading');
          button.setAttribute('aria-busy', 'true');
          addToCart(button.dataset.variantId, 1)
            .then(showCart)
            .catch((e) => {
              if (error) {
                error.textContent = e.message;
                error.hidden = false;
              }
            })
            .finally(() => {
              button.disabled = false;
              button.classList.remove('is-loading');
              button.removeAttribute('aria-busy');
            });
        }
      }
    );
  }

  /* -------------------------------------------------------------------
     Lien de la note : repli vers la première section d'avis
     ------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    var link = event.target.closest('.sn-rating__link');
    if (!link) return;
    var href = link.getAttribute('href') || '';
    if (href.charAt(0) !== '#') return;
    var target = null;
    try {
      target = href.length > 1 ? document.querySelector(href) : null;
    } catch (e) {
      target = null;
    }
    if (!target) target = document.querySelector('.sn-reviews');
    if (!target) return;
    event.preventDefault();
    scrollToElement(target);
    if (history.replaceState && href.length > 1) history.replaceState(null, '', href);
  });

  /* -------------------------------------------------------------------
     Changement de variante (publié par product-info.js de Dawn)
     ------------------------------------------------------------------- */
  function onVariantChange(payload) {
    var data = payload && payload.data;
    if (!data || !data.sectionId) return;
    var root = document.getElementById('MainProduct-' + data.sectionId);
    if (!root) return;

    if (data.html) {
      root.querySelectorAll('[data-sn-refresh]').forEach((el) => {
        var source = data.html.getElementById(el.id);
        if (source) el.innerHTML = source.innerHTML;
      });
      root.querySelectorAll('sn-bundle').forEach((bundle) => {
        if (typeof bundle.restore === 'function') bundle.restore();
      });
    }

    // product-form.js remet « Ajouter au panier » : on reprend le libellé réglé dans l'éditeur.
    var button = root.querySelector('.sn-atc');
    if (button && !button.disabled && button.dataset.label) {
      var label = button.querySelector('.sn-atc__label');
      if (label) label.textContent = button.dataset.label;
    }

    var media = data.variant && data.variant.featured_media;
    var gallery = root.querySelector('sn-gallery');
    if (media && gallery && typeof gallery.goToMedia === 'function') gallery.goToMedia(media.id);
  }

  function subscribeVariantChange() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
      return true;
    }
    return false;
  }

  if (!subscribeVariantChange()) {
    document.addEventListener('DOMContentLoaded', subscribeVariantChange, { once: true });
  }
})();
